// Times a person reads, on the reader's own clock (owner, 2026-10-26).
//
// Storage is UTC (CLAUDE.md "Idő"), and UTC is not on the screen: a moment is
// shown in the browser's own zone — Intl with no `timeZone`. Where a country's
// clock matters too (a campaign's start in that country, its sending window),
// the country's wall clock follows in brackets, named by its city:
// "2026. 10. 30. 08:00 (Bukarest 09:00)".
//
// `viewer` is for the tests only: a page leaves it out and gets the browser's.

const CITY: Record<string, string> = {
  "Europe/Amsterdam": "Amszterdam",
  "Europe/Athens": "Athén",
  "Europe/Berlin": "Berlin",
  "Europe/Bratislava": "Pozsony",
  "Europe/Brussels": "Brüsszel",
  "Europe/Bucharest": "Bukarest",
  "Europe/Budapest": "Budapest",
  "Europe/Dublin": "Dublin",
  "Europe/Ljubljana": "Ljubljana",
  "Europe/London": "London",
  "Europe/Paris": "Párizs",
  "Europe/Prague": "Prága",
  "Europe/Rome": "Róma",
  "Europe/Sofia": "Szófia",
  "Europe/Vienna": "Bécs",
  "Europe/Warsaw": "Varsó",
  "Europe/Zagreb": "Zágráb",
};

/** "Europe/Bucharest" → "Bukarest"; a zone not in the table by its last part. */
export function cityOf(zone: string): string {
  return CITY[zone] ?? (zone.split("/").pop() ?? zone).replace(/_/g, " ");
}

interface Clock {
  year: string;
  month: string;
  day: string;
  /** "17:00" */
  time: string;
}

const FORMATS = new Map<string, Intl.DateTimeFormat>();

function formatFor(zone: string | undefined): Intl.DateTimeFormat {
  const key = zone ?? "";
  let format = FORMATS.get(key);
  if (format === undefined) {
    format = new Intl.DateTimeFormat("en-GB", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    FORMATS.set(key, format);
  }
  return format;
}

/** A moment on a clock; null for an unknown zone name or no moment. */
function clockOf(moment: Date, zone?: string): Clock | null {
  if (Number.isNaN(moment.getTime())) return null;
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = formatFor(zone).formatToParts(moment);
  } catch {
    return null;
  }
  const part = (type: string) => parts.find((entry) => entry.type === type)?.value ?? "00";
  return {
    year: part("year"),
    month: part("month"),
    day: part("day"),
    time: `${part("hour")}:${part("minute")}`,
  };
}

function momentOf(value: string | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "2026. 10. 30. 17:00" on the reader's clock; "—" for no moment. */
export function atViewer(value: string | Date | null | undefined, viewer?: string): string {
  const moment = momentOf(value);
  const clock = moment === null ? null : clockOf(moment, viewer);
  if (clock === null) return "—";
  return `${clock.year}. ${clock.month}. ${clock.day}. ${clock.time}`;
}

/** "10. 30. 17:00" on the reader's clock — a log line's moment. */
export function shortAtViewer(value: string | Date | null | undefined, viewer?: string): string {
  const moment = momentOf(value);
  const clock = moment === null ? null : clockOf(moment, viewer);
  if (clock === null) return "—";
  return `${clock.month}. ${clock.day}. ${clock.time}`;
}

/** "17:00" on the reader's clock. */
export function timeAtViewer(value: string | Date | null | undefined, viewer?: string): string {
  const moment = momentOf(value);
  const clock = moment === null ? null : clockOf(moment, viewer);
  return clock === null ? "—" : clock.time;
}

/** The country's wall clock for the brackets: "Bukarest 09:00", with the
 * month and day when that clock is on another day than the reader's. Null
 * without a zone, or with one Intl does not know. */
export function countryClock(
  value: string | Date | null | undefined,
  zone: string | null | undefined,
  viewer?: string,
): string | null {
  const moment = momentOf(value);
  // "UTC" is the pages' stand-in for a country whose account has not loaded
  // yet: no bracket rather than a UTC one.
  if (moment === null || !zone || zone === "UTC" || zone === "Etc/UTC") return null;
  const there = clockOf(moment, zone);
  const here = clockOf(moment, viewer);
  if (there === null) return null;
  const sameDay =
    here !== null && here.year === there.year && here.month === there.month && here.day === there.day;
  return `${cityOf(zone)} ${sameDay ? "" : `${there.month}. ${there.day}. `}${there.time}`;
}

/** "2026. 10. 30. 08:00 (Bukarest 09:00)" — the reader's clock first, the
 * country's in brackets. Without a zone the reader's clock alone. */
export function withCountryClock(
  value: string | Date | null | undefined,
  zone: string | null | undefined,
  viewer?: string,
): string {
  const here = atViewer(value, viewer);
  const there = countryClock(value, zone, viewer);
  return there === null || here === "—" ? here : `${here} (${there})`;
}

/** Where a counted day ("2026-10-26") starts — the rule and the budget turn
 * over at UTC midnight — said on the reader's clock, the country's in
 * brackets: "a nap nálad 01:00-kor kezdődik (Bukarest 02:00)". */
export function dayStartText(day: string, zone?: string | null, viewer?: string): string {
  const midnight = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(midnight.getTime())) return "";
  const there = countryClock(midnight, zone, viewer);
  const here = timeAtViewer(midnight, viewer);
  return `a nap nálad ${here}-kor kezdődik${there === null ? "" : ` (${there})`}`;
}

const LOCAL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

/** The moment a wall clock ("2026-10-30T09:00") reads in `zone`, or null.
 * The zone's offset is taken at the moment itself, so a date on the other
 * side of a daylight-saving switch gets its own offset. */
export function wallToMoment(local: string, zone: string): Date | null {
  const match = LOCAL.exec(local);
  if (match === null) return null;
  const [, year, month, day, hour, minute] = match.map(Number) as number[];
  const asUtc = Date.UTC(year!, month! - 1, day!, hour!, minute!);
  const offsetAt = (instant: number): number | null => {
    const clock = clockOf(new Date(instant), zone);
    if (clock === null) return null;
    const [h, m] = clock.time.split(":").map(Number);
    return Date.UTC(Number(clock.year), Number(clock.month) - 1, Number(clock.day), h, m) - instant;
  };
  const first = offsetAt(asUtc);
  if (first === null) return null;
  const second = offsetAt(asUtc - first);
  return new Date(asUtc - (second ?? first));
}
